import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import {
  PLATFORM_REPORT_ROUTES,
  type PlatformReport,
  type PlatformReportCode,
  type ReportCellValue,
  type ReportParameterName,
  type ReportParameterValues,
  type ReportRow,
} from '@/types/platform';
import { formatCount, formatDateTime } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * PA-18 — the operational reports over platform-administration data.
 *
 * THE INDEX IS BUILT HERE AND THAT IS DELIBERATE. There is no
 * `GET /platform/reports`, and this screen used to ask for one and report the
 * whole feature as undeployed while all twelve reports were live. Asking core to
 * add an index would be asking it to restate something the console already knows:
 * the route set is fixed at compile time, one method per report, and the console
 * cannot open a report it has no renderer for anyway. So the list of reports is
 * local — see PLATFORM_REPORT_ROUTES — and everything the SERVER owns about a
 * report arrives in that report's own metadata and is rendered from there.
 *
 * THERE IS NO RUN HISTORY AND NO "COMING SOON" PLACEHOLDER FOR ONE. Every route
 * on that controller is a GET computed from live tables; core states plainly that
 * a reporting endpoint which records that it ran is one an operator triggers twice
 * by refreshing a dashboard. A permanent "not available yet" panel would promise a
 * feature nobody intends to build, which is a worse lie than a 404. The screen
 * says instead that reports are computed on demand.
 *
 * THE METADATA IS THE REPORT. `scope.notCounted`, `unmeasured` and the authority
 * disclaimer are rendered above the table, not tucked into a footnote: a figure
 * from this service is not EduCore's academic record and not anybody's financial
 * position, and a reader who reconciles against it without seeing that will find a
 * discrepancy that is real and unexplainable from this side. A report whose
 * envelope arrives without metadata is refused rather than rendered.
 */

const PARAMETER_INPUT_KIND: Record<ReportParameterName, 'date' | 'number'> = {
  from: 'date',
  to: 'date',
  stalledForAtLeastDays: 'number',
  expiringWithinDays: 'number',
};

function isReportCode(value: string | null): value is PlatformReportCode {
  return PLATFORM_REPORT_ROUTES.some((route) => route.code === value);
}

export function ReportsPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const selectedParam = params.get('report');
  const selected: PlatformReportCode | null = isReportCode(selectedParam) ? selectedParam : null;

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('platform.reports.title')}</h1>
          <p className={styles.pageLead}>{t('platform.reports.lead')}</p>
        </div>
      </header>

      <p className={styles.dataNotice}>
        <Icon name="info" size={15} aria-hidden="true" />
        {t('platform.reports.onDemandNotice')}
      </p>

      <Panel title={t('platform.reports.availableTitle')} icon="checklist">
        <ul className={styles.rowList}>
          {PLATFORM_REPORT_ROUTES.map((route) => (
            <li key={route.code} className={styles.row}>
              <div className={styles.rowMain}>
                <p className={styles.rowTitle}>
                  {t(`platform.reports.report.${route.code}.name`, { defaultValue: route.code })}
                </p>
                <p className={styles.rowMetaLine}>
                  <code className={styles.code}>{route.code}</code>
                  {route.parameters.length > 0
                    ? ` · ${t('platform.reports.takesParameters', {
                        parameters: route.parameters
                          .map((name) => t(`platform.reports.parameter.${name}`))
                          .join(', '),
                      })}`
                    : ` · ${t('platform.reports.noParameters')}`}
                </p>
                <p className={styles.rowDetail}>
                  {t(`platform.reports.report.${route.code}.question`, { defaultValue: '' })}
                </p>
              </div>
              <div className={styles.rowAside}>
                <button
                  type="button"
                  className={selected === route.code ? styles.primaryBtnSm : styles.secondaryBtn}
                  aria-pressed={selected === route.code}
                  onClick={() => setParams({ report: route.code })}
                >
                  {t('platform.reports.open')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </Panel>

      {selected && <ReportView code={selected} onClose={() => setParams({})} />}
    </div>
  );
}

function ReportView({ code, onClose }: { code: PlatformReportCode; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const route = PLATFORM_REPORT_ROUTES.find((candidate) => candidate.code === code);

  // Draft values are what the operator is typing; applied values are what the
  // last run used. Keeping them apart stops a half-typed date from firing a
  // request on every keystroke and from being shown as the window that was used.
  const [draft, setDraft] = useState<ReportParameterValues>({});
  const [applied, setApplied] = useState<ReportParameterValues>({});

  useEffect(() => {
    setDraft({});
    setApplied({});
  }, [code]);

  const signature = useMemo(() => JSON.stringify(applied), [applied]);
  const fetcher = useCallback(() => platformApi.reports.run(code, applied), [code, applied]);
  const { state, reload } = useAsyncData<PlatformReport>(`report:${code}:${signature}`, fetcher);

  if (!route) return null;

  return (
    <Panel
      title={t(`platform.reports.report.${code}.name`, { defaultValue: code })}
      icon="platform"
      actions={
        <>
          <button type="button" className={styles.secondaryBtn} onClick={reload}>
            <Icon name="refresh" size={15} />
            {t('platform.reports.recompute')}
          </button>
          <button type="button" className={styles.secondaryBtn} onClick={onClose}>
            {t('common.close')}
          </button>
        </>
      }
    >
      {route.parameters.length > 0 && (
        <form
          className={styles.filterBar}
          onSubmit={(event) => {
            event.preventDefault();
            setApplied(draft);
          }}
        >
          {route.parameters.map((name) => (
            <div key={name} className={styles.filterField}>
              <label className={styles.filterLabel} htmlFor={`report-${name}`}>
                {t(`platform.reports.parameter.${name}`)}
              </label>
              <input
                id={`report-${name}`}
                className={styles.input}
                type={PARAMETER_INPUT_KIND[name]}
                value={draft[name] ?? ''}
                onChange={(event) => setDraft({ ...draft, [name]: event.target.value })}
              />
            </div>
          ))}
          <button type="submit" className={styles.secondaryBtn}>
            <Icon name="search" size={15} />
            {t('platform.reports.apply')}
          </button>
        </form>
      )}

      <AsyncPanel state={state} onRetry={reload}>
        {(report) => (
          <>
            <ReportMetadata report={report} locale={locale} />
            <ReportTable report={report} locale={locale} />
          </>
        )}
      </AsyncPanel>
    </Panel>
  );
}

function ReportMetadata({ report, locale }: { report: PlatformReport; locale: string }) {
  const { t } = useTranslation();
  const { metadata } = report;

  return (
    <>
      <p className={styles.rowDetail}>{metadata.question}</p>

      {/*
        The authority disclaimer sits above the numbers, not below them. It is the
        difference between "the platform says 412 institutions hold this product"
        and "412 institutions are entitled to it, which is not the same as 412
        institutions using it, and is nobody's billing position".
      */}
      <div className={styles.warningNotice} role="note">
        <Icon name="info" size={16} aria-hidden="true" />
        <div>
          <p className={styles.warningTitle}>{t('platform.reports.authorityTitle')}</p>
          <p className={styles.warningBody}>{metadata.authorityDisclaimer}</p>
        </div>
      </div>

      <dl className={styles.detailGrid}>
        <dt>{t('platform.reports.generatedAt')}</dt>
        <dd>{formatDateTime(metadata.generatedAt, locale)}</dd>
        <dt>{t('platform.reports.freshness')}</dt>
        <dd>{metadata.freshness.explanation}</dd>
        <dt>{t('platform.reports.window')}</dt>
        <dd>
          {metadata.window
            ? `${formatDateTime(metadata.window.from, locale)} → ${formatDateTime(metadata.window.to, locale)}`
            : t('platform.reports.allHistory')}
        </dd>
        <dt>{t('platform.reports.rowCount')}</dt>
        <dd>{formatCount(metadata.rowCount, locale)}</dd>
        <dt>{t('platform.reports.sources')}</dt>
        <dd>
          {metadata.sources.length === 0
            ? '—'
            : metadata.sources.map((source) => `${source.table} (${source.ownedBy})`).join(', ')}
        </dd>
        <dt>{t('platform.reports.counted')}</dt>
        <dd>{listOrDash(metadata.scope.counted)}</dd>
        {/*
          What was NOT counted is the half of the scope a reader would otherwise
          assume away. It gets the same weight as what was.
        */}
        <dt>{t('platform.reports.notCounted')}</dt>
        <dd>{listOrDash(metadata.scope.notCounted)}</dd>
      </dl>

      {metadata.scope.notes.length > 0 && (
        <ul className={styles.impactList}>
          {metadata.scope.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}

      {/*
        A figure this service will not invent. Rendered as its own block because
        NOT_MEASURED is not zero, and a table that quietly omitted the row would
        read as "there is nothing there".
      */}
      {metadata.unmeasured.length > 0 && (
        <section>
          <h3 className={styles.subheading}>{t('platform.reports.unmeasuredTitle')}</h3>
          <ul className={styles.ruleList}>
            {metadata.unmeasured.map((entry) => (
              <li key={entry.fact} className={styles.ruleItem}>
                <p className={styles.rowTitle}>{entry.fact}</p>
                <p className={styles.rowMetaLine}>{entry.reason}</p>
                <p className={styles.rowDetail}>
                  {t('platform.reports.wouldRequire', { detail: entry.wouldRequire })}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {metadata.warnings.length > 0 && (
        <ul className={styles.impactListWarning}>
          {metadata.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
    </>
  );
}

function ReportTable({ report, locale }: { report: PlatformReport; locale: string }) {
  const { t } = useTranslation();

  /*
   * Columns come from the rows, in the order the server sent them, rather than
   * from a per-report list held here. A report that gains a column shows it
   * without a redeploy; more importantly, a column the server stops sending
   * disappears instead of rendering as an empty cell that reads like a zero.
   */
  const columns = useMemo(() => {
    const seen: string[] = [];
    for (const row of report.rows) {
      for (const key of Object.keys(row)) {
        if (!seen.includes(key)) seen.push(key);
      }
    }
    return seen;
  }, [report.rows]);

  if (report.rows.length === 0) {
    return (
      <p className={styles.sectionEmpty} role="status">
        {t('platform.reports.noRows')}
      </p>
    );
  }

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <caption className={styles.tableCaption}>
          {t('platform.reports.rowsCaption', { n: report.rows.length })}
        </caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column} scope="col">
                {t(`platform.reports.column.${column}`, { defaultValue: humanise(column) })}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.rows.map((row, index) => (
            <tr key={rowKey(row, index)}>
              {columns.map((column, columnIndex) =>
                columnIndex === 0 ? (
                  <th key={column} scope="row" className={styles.tableRowHeader}>
                    {renderCell(row[column], locale, t)}
                  </th>
                ) : (
                  <td key={column}>{renderCell(row[column], locale, t)}</td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A stable key without assuming any particular id column exists. */
function rowKey(row: ReportRow, index: number): string {
  const candidate = row.caseId ?? row.integrationId ?? row.institutionId ?? row.bucket ?? row.status;
  return typeof candidate === 'string' ? candidate : String(index);
}

function humanise(column: string): string {
  return column
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (character) => character.toUpperCase())
    .trim();
}

/**
 * Renders one cell without ever turning "not measured" into a number.
 *
 * A `Measurement` arrives as `{ status, value }` or `{ status, reason }`, and the
 * NOT_MEASURED case must read as words rather than as a blank cell — a blank next
 * to "average hours" is indistinguishable from zero hours, which is the one
 * reading core built the whole envelope to prevent.
 */
function renderCell(
  value: ReportCellValue | undefined,
  locale: string,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (value === undefined || value === null) return '—';
  if (typeof value === 'number') return formatCount(value, locale);
  if (typeof value === 'boolean') return value ? t('platform.lifecycle.impact.yes') : t('platform.lifecycle.impact.no');
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.length > 0 ? value.map(String).join(', ') : '—';

  const record = value as Record<string, unknown>;
  if (record.status === 'NOT_MEASURED') {
    return t('platform.reports.notMeasured', {
      reason: typeof record.reason === 'string' ? record.reason : '',
    });
  }
  if (record.status === 'MEASURED') {
    const measured = record.value;
    return typeof measured === 'number' ? formatCount(measured, locale) : String(measured ?? '—');
  }
  // A keyed breakdown — institutionsByState, registrationsByState — reads better
  // inline than as JSON, and JSON in a table cell is how a number gets misread.
  return Object.entries(record)
    .map(([key, entry]) => `${key}: ${entry ?? '—'}`)
    .join(' · ');
}

function listOrDash(values: readonly string[]): string {
  return values.length > 0 ? values.join(', ') : '—';
}
